import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Globe, ImageIcon, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { applicationService } from '@/services/storage';
import { uploadImage } from '@/services/upload';
import type { Application } from '@/types';
import { toast } from 'sonner';

// 图片上传配置 - 无大小限制
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

export default function AdminApplications() {
  const [applications, setApplications] = useState<Application[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<Application | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    titleEn: '',
    description: '',
    image: '',
  });
  const [errorDialog, setErrorDialog] = useState<{open: boolean; title: string; message: string}>({
    open: false,
    title: '',
    message: ''
  });

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    setApplications(await applicationService.getAll());
  };

  const showError = (title: string, message: string) => {
    setErrorDialog({ open: true, title, message });
  };

  const validateImage = (file: File): boolean => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      showError(
        '文件类型错误',
        `请上传图片文件（支持 ${ALLOWED_TYPES.map(t => t.replace('image/', '').toUpperCase()).join('、')} 格式）`
      );
      return false;
    }
    // 无文件大小限制
    return true;
  };

  const handleAdd = async () => {
    setEditingApp(null);
    setFormData({
      title: '',
      titleEn: '',
      description: '',
      image: '',
    });
    setDialogOpen(true);
  };

  const handleEdit = async (app: Application) => {
    setEditingApp(app);
    setFormData({
      title: app.title,
      titleEn: app.titleEn,
      description: app.description,
      image: app.image || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('确定要删除此应用领域吗？')) {
      await applicationService.delete(id);
      toast.success('应用领域已删除');
      loadApplications();
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[上传调试] 应用领域 - 触发 onChange 事件', e.target.files);
    const file = e.target.files?.[0];
    if (!file) {
      console.log('[上传调试] 未选择文件');
      return;
    }

    console.log('[上传调试] 应用领域 - 选择的文件:', file.name, file.type, file.size);

    if (!validateImage(file)) {
      console.log('[上传调试] 验证失败');
      return;
    }

    try {
      console.log('[上传调试] 应用领域 - 开始上传到服务器...');
      const imageUrl = await uploadImage(file);
      console.log('[上传调试] 应用领域 - 上传成功, URL:', imageUrl);
      setFormData({ ...formData, image: imageUrl });
      toast.success('图片上传成功');
    } catch (error) {
      console.error('[上传调试] 应用领域 - 上传失败:', error);
      showError('上传失败', error instanceof Error ? error.message : '图片上传失败，请重试');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      showError('缺少标题', '请填写标题');
      return;
    }

    if (editingApp) {
      await applicationService.update(editingApp.id, formData);
      toast.success('应用领域已更新');
    } else {
      const newApp: Application = {
        id: Date.now().toString(),
        ...formData,
      };
      await applicationService.add(newApp);
      toast.success('应用领域已添加');
    }

    setDialogOpen(false);
    loadApplications();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">应用领域管理</h1>
        <Button onClick={handleAdd}>
          <Plus className="mr-2 h-5 w-5" />
          添加应用领域
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>图片</TableHead>
                <TableHead>标题</TableHead>
                <TableHead>英文标题</TableHead>
                <TableHead>描述</TableHead>
                <TableHead className="text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {applications.map((app) => (
                <TableRow key={app.id}>
                  <TableCell>
                    {app.image ? (
                      <img
                        src={app.image}
                        alt={app.title}
                        className="w-16 h-12 object-cover rounded"
                      />
                    ) : (
                      <div className="w-16 h-12 bg-gray-200 rounded flex items-center justify-center">
                        <Globe className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{app.title}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-500">{app.titleEn}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-500 max-w-xs truncate">
                      {app.description}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(app)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(app.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {applications.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              暂无应用领域数据
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingApp ? '编辑应用领域' : '添加应用领域'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">
                  标题 <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="请输入标题"
                />
              </div>
              <div>
                <Label htmlFor="titleEn">英文标题</Label>
                <Input
                  id="titleEn"
                  value={formData.titleEn}
                  onChange={(e) => setFormData({ ...formData, titleEn: e.target.value })}
                  placeholder="请输入英文标题"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="description">描述</Label>
              <textarea
                id="description"
                className="w-full border rounded-md px-3 py-2"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="请输入描述"
              />
            </div>
            <div>
              <Label htmlFor="image">
                <span className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  配图
                </span>
              </Label>
              <div className="mt-2">
                {formData.image && (
                  <img
                    src={formData.image}
                    alt="Application"
                    className="w-full h-32 object-cover rounded mb-2"
                  />
                )}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
                <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                取消
              </Button>
              <Button type="submit">
                <Globe className="mr-2 h-4 w-4" />
                保存
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={errorDialog.open} onOpenChange={(open) => setErrorDialog({ ...errorDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              {errorDialog.title}
            </DialogTitle>
            <DialogDescription>
              {errorDialog.message}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={() => setErrorDialog({ ...errorDialog, open: false })}>
              知道了
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
